import React, { useEffect, useState } from 'react';

import { standardSchemaResolver } from '@hookform/resolvers/standard-schema';
import { IconCornerDownRight, IconPlus } from '@tabler/icons-react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { z } from 'zod';

import { Button, Icon, TextField } from '@cfa/react-components';

import Constants from '@/constants';

import { queryClient } from '@/services';
import { IntlCategoryEntity } from '@/services/content-api-types';

import 'react-smartbanner/dist/main.css';

import AddIcon from '../AddIcon';
import { addCategory } from '../MainOverview/actions';

import { getCategoryIconsOptions } from '@/_generated/api-clients/content';
import { useAdminManagingCountry } from '@/routes/Admin/useAdminManagingCountry';

const addCategoryFormSchema = (translationNotRequired?: boolean) =>
  z
    .object({
      name: z.string().min(1, { error: 'Name is required' }),
      translation: z.string().optional(),
      icon: z.string().min(1, { error: 'Icon is required' }),
    })
    .superRefine((data, ctx) => {
      if (translationNotRequired) {
        return;
      }

      if (!data.translation?.trim()) {
        ctx.addIssue({
          code: 'custom',
          message: 'Translation is required',
          path: ['translation'],
        });
      }
    });

type AddCategoryFormValues = z.infer<ReturnType<typeof addCategoryFormSchema>>;

interface AddCategoryProps {
  translationNotRequired?: boolean;
  isRecommended?: boolean;
  isSubcategory?: boolean;
  onCancel?: () => void;
  onClose?: () => void;
  numberOfCategories: number;
}

export const AddCategory: React.FC<AddCategoryProps> = ({
  translationNotRequired,
  isRecommended = false,
  isSubcategory = false,
  onCancel,
  onClose,
  numberOfCategories,
}) => {
  const { t } = useTranslation();
  const { selectedCountry } = useAdminManagingCountry();
  const [showAddIconPopUp, setShowAddIconPopUp] = useState(false);

  // prefetch icons
  useEffect(() => {
    queryClient.prefetchQuery(getCategoryIconsOptions());
  }, []);

  const {
    register,
    handleSubmit,
    setValue,
    reset,
    watch,
    formState: { isValid },
  } = useForm<AddCategoryFormValues>({
    resolver: standardSchemaResolver(
      addCategoryFormSchema(translationNotRequired),
    ),
  });

  const onSubmit = (form: AddCategoryFormValues) => {
    const newCategory: IntlCategoryEntity = {
      country: selectedCountry.label,
      displayIndex: numberOfCategories,
      enabled: false,
      featured: isRecommended,
      icon: form.icon,
      name: {
        en: form.name,
        es: form.translation ?? '',
      },
    };

    onClose?.();
    addCategory(newCategory, isRecommended, selectedCountry.label);
  };

  const handleCancel = () => {
    onCancel?.();
    reset();
  };

  const handleIconAdd = (icon: string) => {
    setValue('icon', icon, { shouldValidate: true });
    setShowAddIconPopUp(false);
  };

  // eslint-disable-next-line react-hooks/incompatible-library
  const icon = watch('icon');

  return (
    <div className="border-grayscale-gray2 shadow-elevation-5 relative mb-4 rounded border p-4">
      <div className="flex flex-row">
        {!isSubcategory && (
          <div className="mr-4 flex flex-col items-center justify-start">
            <span
              data-testid="AdminAddIconWrapper"
              className="mb-2 text-sm font-medium whitespace-nowrap"
            >
              {t('Admin.addIcon')}
              <span className="text-feedback-error-base">*</span>
            </span>
            {icon ? (
              <button
                type="button"
                data-testid="Icon"
                onClick={() => setShowAddIconPopUp(true)}
                className="size-10 cursor-pointer border-none bg-transparent p-0"
              >
                <img
                  alt={icon}
                  src={`${Constants.PATHWAY_CDN_IMG.CATEGORIES}${icon}.svg`}
                  className="size-full"
                />
              </button>
            ) : (
              <Icon
                data-testid="AddIcon"
                icon={IconPlus}
                size="md"
                onClick={() => setShowAddIconPopUp(true)}
                className="cursor-pointer!"
              />
            )}
          </div>
        )}
        <div className="flex grow flex-col">
          <TextField
            data-testid="AddCategoryNameInput"
            fullWidth
            label={
              isSubcategory
                ? t('Admin.subcategory')
                : isRecommended
                  ? t('Admin.recommendedCategory')
                  : t('Admin.category')
            }
            placeholder="Enter Name"
            required
            {...register('name')}
          />
          <div className="mt-4 flex flex-row">
            <IconCornerDownRight size={35} />
            <div className="ml-2 flex grow flex-col">
              <TextField
                data-testid="AddCategoryTranslationInput"
                fullWidth
                label={t('Admin.spanishTranslation')}
                placeholder="Enter Translation"
                required={!translationNotRequired}
                {...register('translation', {
                  setValueAs: (v: unknown) => {
                    const str = v as string;
                    return str.trim() === '' ? undefined : v;
                  },
                })}
              />
            </div>
          </div>
        </div>
      </div>
      <div className="flex flex-row">
        <div className="mt-4 flex">
          <Button
            color="secondary"
            variant="outlined"
            className="mr-2!"
            onClick={handleCancel}
          >
            {t('Button.cancel')}
          </Button>
          <Button
            color="secondary"
            data-testid="AddButton"
            disabled={!isValid}
            onClick={handleSubmit(onSubmit)}
          >
            {t('Button.add')}
          </Button>
        </div>
      </div>
      {showAddIconPopUp && (
        <AddIcon
          className="absolute top-[75px] left-[32px] z-5 translate-[unset]!"
          onAddIcon={handleIconAdd}
          onClose={() => setShowAddIconPopUp(false)}
        />
      )}
    </div>
  );
};

export default AddCategory;
